import { z } from 'zod';

import { attachmentSchema } from './attachment.schema';
import { BlockVisibilityOutputSchema, DbSubmissionBlockSchema, OpenSearchBlockSchema } from './block.schema';
import { BlockError } from './block-error';
import type { PK } from './database.schema';
import { baseDatabaseSchema, recordUserSchema, userSchema } from './database.schema';
import { batchCloneJobHeaderSchema } from './endorsement-batch-clone.schema';
import { EndorsementPremiumChange } from './endorsement-premium-change';
import { DbSummaryBlockSchema, DbSummarySectionSchema } from './endorsement-summary.schema';
import { DbSectionSchema } from './section.schema';

export enum ResourceState {
  DRAFT = 'DRAFT',
  REVIEW = 'REVIEW',
  FIRM_ORDER = 'FIRM_ORDER',
  FINAL = 'FINAL',
  ARCHIVED = 'ARCHIVED',
  PUBLISHED = 'PUBLISHED',
  PRE_DRAFT = 'PRE_DRAFT',
}

export const DbEndorsementStatusSchema = z.enum([
  ResourceState.DRAFT,
  ResourceState.REVIEW,
  ResourceState.FINAL,
  ResourceState.ARCHIVED,
]);

export const DbSubmissionStatusSchema = z.enum([
  ResourceState.DRAFT,
  ResourceState.REVIEW,
  ResourceState.FIRM_ORDER,
  ResourceState.FINAL,
  ResourceState.ARCHIVED,
  ResourceState.PRE_DRAFT,
]);

export const NON_PRE_DRAFT_SUBMISSION_STATUSES = DbSubmissionStatusSchema.options.filter(
  (status) => status !== ResourceState.PRE_DRAFT,
);

export const DbTemplateStatusSchema = z.enum([
  ResourceState.DRAFT,
  ResourceState.REVIEW,
  ResourceState.PUBLISHED,
  ResourceState.ARCHIVED,
]);

export const DbContractSchema = z
  .object({
    attachments: z.array(attachmentSchema).optional(),
    name: z.string(),
    status: z.nativeEnum(ResourceState),
    version: z.number(),
    data_items: z.any(), // this is covered by AJV validation
    ng_default_section: z.any().optional(), // when syncing from NG, we need to retain the full default section
    layout: z.array(z.string()),
    attachments_layout: z.array(z.string()).optional(),
    document_name: z.string().optional(), // deprecated, can we remove it?
    document_type: z.string().optional(), // why do we need this? we only have one type which is MRC
    document_version: z
      .number()
      .optional()
      .describe('@deprecated Attr `document_version` is deprecated - use `version` from `documents` instead'), // Isn't this duplicated inside of documents?
    documents: z
      .array(
        z
          .object({
            name: z.string(),
            subtype: z.string().optional(),
            type: z.enum([
              'template-document',
              'submission-document',
              'endorsement-document',
              'endorsement-summary',
              'branding-document',
              'submission-comparison-document',
            ]),
            version: z.number(),
            path: z.string().optional(),
          })
          .strict(),
      )
      .optional(), // should this be an array? won't there always just be one document per resource type?
    sourceResourceId: z.string().uuid(),
    sourceResourceName: z.enum(['templates', 'submissions']),
    sourceResourceDisplayName: z.string().optional(),
    /**
     * Number for PRO-clients and string for PPL
     */
    teams: z.array(z.union([z.string(), z.number()])).optional(),
    tags: z.array(z.string()).optional(),
    branding: z.string().optional(),
    branding_id: z.string().uuid().optional(),
    class_of_business: z.string().optional(),
    external_id: z.string().nullable().optional(), // used for external PRO-clients API; nullable once clearable via PATCH
    transaction_status: z.enum(['pending', 'complete']).optional(),
    endorsementsCount: z.number().optional(),
    unresolvedCommentsCount: z.number().optional(),
    renewals: z.array(z.string()).optional(),
    renewalParentId: z.string().optional(),
    /**
     * Merge receipt written at renewal creation — the endorsements merged in, in applied order
     */
    mergedEndorsements: z.array(z.object({ id: z.string(), name: z.string() })).optional(),
    /**
     * If submission has changes that has not been synced to the external source
     */
    is_pending_sync: z.boolean().optional(),
    /**
     * Hash of the data in the NextGen external source
     */
    ng_data_hash: z.string().optional(),
    last_template_swapped_at: z.string().optional(),
    last_synced_at: z.string().optional(),
    contract_validation_checks: z.boolean().optional(),
    contract_missing_data: z.array(z.object({ id: z.string(), title: z.string() })).optional(),
    /**
     * Contract Review Bot: set the first time an eval run is started on this
     * contract, never unset. Eval runs hang off the contract's own partition,
     * so this flag on the root row is what lets the tenant-wide export find
     * the reviewed contracts from the `submissions` partition without a table
     * scan. Goes away with the rest of the eval feature.
     */
    has_eval_runs: z.boolean().optional(),
    /**
     * Contract Guidance counters, kept on the root row so the contracts list,
     * OpenSearch and the CSV export can show and filter guidance without
     * reading eval-run rows. Absent until a guidance scan has completed on
     * this contract — "absent" is what the has / has-no guidance filter keys
     * on, so never default these to 0 when indexing.
     *
     *  - `guidanceRunId` — the completed run the counts describe. Writes from
     *    any other run are rejected, so a stale review cannot overwrite them.
     *  - `guidanceFindingsCount` — canonical (de-duplicated) findings of that run.
     *  - `guidanceReviewedCount` — of those, resolved or dismissed, by a user
     *    or by the AI re-check.
     *  - `guidanceRevision` — bumped on every counter write; part of the
     *    OpenSearch external document version, so a counter change reaches
     *    the index without bumping the contract's own `version` (which would
     *    regenerate PDFs and fire integrations).
     *
     * Maintained by `backend/domain/evals/guidanceCounts.ts`.
     */
    guidanceRunId: z.string().optional(),
    guidanceFindingsCount: z.number().int().nonnegative().optional(),
    guidanceReviewedCount: z.number().int().nonnegative().optional(),
    guidanceRevision: z.number().int().nonnegative().optional(),

    // to be removed
    ref: z.string(), // i'd consider removing this, raise with JP
    schema_id: z.string().optional(), // remove ASAP
    revision_id: z.string().optional(), // remove
    revision_name: z.string().optional(), // remove
    revision_created_at: z.string().optional(), // remove
    umr: z.string().optional(), // remove
    tenant_id: z.string().optional(), // remove
  })
  .merge(baseDatabaseSchema);

export const DbSubmissionSchema = DbContractSchema.extend({
  status: DbSubmissionStatusSchema,
});

export const DbTemplateSchema = DbContractSchema.extend({
  status: DbTemplateStatusSchema,
});

export const DbContractFilteredSchema = DbSubmissionSchema.pick({
  pk: true,
  sk: true,
  id: true,
  name: true,
  external_id: true,
  status: true,
  transaction_status: true,
  ref: true,
  branding: true,
  branding_id: true,
  class_of_business: true,
  endorsementsCount: true,
  unresolvedCommentsCount: true,
  guidanceFindingsCount: true,
  guidanceReviewedCount: true,
  guidanceRevision: true,
  sourceResourceDisplayName: true,
  tags: true,
  teams: true,
  created_at: true,
  updated_at: true,
  contract_missing_data: true,
  sourceResourceName: true,
  sourceResourceId: true,
  renewals: true,
  renewalParentId: true,
}).extend({
  created_by: userSchema.pick({ name: true }),
  last_updated_by: userSchema.pick({ name: true }).optional(),
  data_items: z.object({
    risk_details: z
      .object({
        unique_market_reference: z
          .object({
            unique_market_reference: z.string().optional(),
          })
          .optional(),
      })
      .optional(),
  }),
});

export const DbContractFilteredCsvSchema = DbContractFilteredSchema.omit({
  sourceResourceId: true,
});

export const OpenSearchSubmissionsDataSchema = DbContractFilteredSchema.omit({
  created_by: true,
  last_updated_by: true,
}).extend({
  contract_missing_data_count: z.number().optional(),
  umr: z.string().optional(),
  unresolvedCommentsCount: z.number(),
  created_by: z.string(),
  last_updated_by: z.string().optional(),
  endorsementsCount: z.number(),
  renewalsCount: z.number().optional(),
  version: z.number(),
  blocks: z.array(OpenSearchBlockSchema),
});

export const OpenSearchStreamHandlerSubmissionsDataSchema = OpenSearchSubmissionsDataSchema.omit({
  blocks: true,
}).extend({
  deleted_at: z.string().optional(),
});

export const GetSubmissionsResponseDataSchema = OpenSearchSubmissionsDataSchema.omit({
  version: true,
  data_items: true,
  blocks: true,
  contract_missing_data: true,
});

export const DbTemplateFilteredSchema = DbContractSchema.pick({
  pk: true,
  sk: true,
  id: true,
  name: true,
  status: true,
  transaction_status: true,
  ref: true,
  branding: true,
  branding_id: true,
  class_of_business: true,
  sourceResourceDisplayName: true,
  tags: true,
  teams: true,
  created_at: true,
  updated_at: true,
  sourceResourceId: true,
  data_items: true,
}).extend({
  created_by: userSchema.pick({ name: true }),
  last_updated_by: userSchema.pick({ name: true }).optional(),
  has_default_content: z.boolean().optional(),
});

export const DbTemplateFilteredCsvSchema = DbTemplateFilteredSchema;

export const DbContractSkeletonSchema = DbContractSchema.partial({
  sourceResourceId: true,
  sourceResourceName: true,
});

export const DbEndorsementSchema = DbContractSchema.extend({
  status: DbEndorsementStatusSchema,
  additional_premium: z.object({ amount: z.number(), currency: z.string() }).nullable().optional(),
  effective_date: z.string().datetime({ local: true }), // should this be optional?
  expiry_date: z.string().datetime({ local: true }).optional(), // should this be optional?
  premium_change: z.nativeEnum(EndorsementPremiumChange).optional(), // should this be optional?
  reason: z.string().optional(), // should this be optional?
  reference: z.string().optional(), // should this be optional?
  summary_layout: z.array(z.string()).optional(),
  blocks_excluded_from_diff: z.array(z.string()).optional(),
  type: z.string().optional(), // this is an enum?
  summary: z.object({
    blocksExcludedFromPreview: z.array(z.string()).default([]),
    layout: z.array(z.string()).default([]),
  }),
  internal_description: z.string().optional(),
  endorsement_summary_template_id: z.number().optional(),
  duplicatedFrom: z
    .object({
      endorsementId: z.string(),
      endorsementName: z.string(),
      submissionId: z.string(),
      submissionName: z.string(),
    })
    .optional(),

  // to be removed
  endorsement_amendment_text: z.string().optional(), // deprecated?
  endorsement_summary: z.string().optional(), // deprecated?
  parentId: z.string(), // deprecated?
}).strict();

/*
 * Response Schemas for Submission and Template Resources
 */
export const BlockResponseSchema = DbSubmissionBlockSchema.extend({
  content_transformed: z.string().optional(),
  validationErrors: z.array(z.nativeEnum(BlockError)).optional(),
  commentsCount: z.number().optional(),
  // From the linked library block (fillLibraryData)
  created_at_in_library: z.string().optional(),
  created_by_in_library: recordUserSchema.optional(),
  // A block an endorsement removes, or hides, relative to its parent contract
  isDeleted: z.boolean().optional(),
  linkedDatapointsFailingValidation: z.array(z.string()).optional(),
  visibilityComputed: z
    .object({
      block: BlockVisibilityOutputSchema.optional(),
      variations: z.array(BlockVisibilityOutputSchema).optional(),
    })
    .optional(),
});
export const SectionResponseSchema = DbSectionSchema.extend({
  blocks: z.array(BlockResponseSchema),
});
export const ContractResponseSchema = DbContractSchema.extend({
  sections: z.array(SectionResponseSchema),
  contract_number_of_required_data_points: z.number().optional(),
});

/**
 * Response Schemas for Endorsement Resources
 */
export const EndorsementResponseWithPartialSummarySchema = DbEndorsementSchema.extend({
  sections: z.array(SectionResponseSchema),
});

export const SummaryBlockResponseSchema = DbSummaryBlockSchema.extend({
  content_transformed: z.string().optional(),
  isDeleted: z.boolean().optional(),
});
export const SummarySectionResponseSchema = DbSummarySectionSchema.extend({
  blocks: z.array(SummaryBlockResponseSchema),
});
export const EndorsementResponseSchema = EndorsementResponseWithPartialSummarySchema.extend({
  batch_clone_jobs: z.array(batchCloneJobHeaderSchema).optional(),
  hasMaterialChanges: z.boolean().optional(),
  summary: z.object({
    blocksExcludedFromPreview: z.array(z.string()).default([]),
    layout: z.array(z.string()).default([]),
    sections: z.array(SummarySectionResponseSchema),
  }),
});

export type DbEndorsementStatus = z.infer<typeof DbEndorsementStatusSchema>;
export type DbSubmissionStatus = z.infer<typeof DbSubmissionStatusSchema>;
export type DbTemplateStatus = z.infer<typeof DbTemplateStatusSchema>;
export type DbContract = z.infer<typeof DbContractSchema> & PK;
export type PdfDocumentType = NonNullable<DbContract['documents']>[number]['type'];
export type DbContractFiltered = z.infer<typeof DbContractFilteredSchema> & PK;
export type GetSubmissionsResponseData = z.infer<typeof GetSubmissionsResponseDataSchema> & PK;
export type OpenSearchSubmissionsData = z.infer<typeof OpenSearchSubmissionsDataSchema> & PK;
export type OpenSearchStreamHandlerSubmissionsData = z.infer<typeof OpenSearchStreamHandlerSubmissionsDataSchema> & PK;
export type DbContractFilteredCsv = z.infer<typeof DbContractFilteredCsvSchema> & PK;
export type DbTemplateFiltered = z.infer<typeof DbTemplateFilteredSchema> & PK;
export type DbTemplateFilteredCsv = z.infer<typeof DbTemplateFilteredCsvSchema> & PK;
export type DbContractSkeleton = z.infer<typeof DbContractSkeletonSchema>;
export type DbTemplate = z.infer<typeof DbTemplateSchema> & PK;
export type DbSubmission = z.infer<typeof DbSubmissionSchema> & PK;
export type DbEndorsement = z.infer<typeof DbEndorsementSchema> & PK;
export type DbSection = z.infer<typeof DbSectionSchema> & PK;
export type BlockResponse = z.infer<typeof BlockResponseSchema>;
// The response schemas type `pk` as a string; `PK` narrows it to a partition key, as the Db types do.
export type ResourceBlock = BlockResponse & PK;
export type SectionResponse = z.infer<typeof SectionResponseSchema>;
export type ResourceSection = Omit<SectionResponse, 'blocks'> & PK & { blocks: ResourceBlock[] };
export type ContractResponse = z.infer<typeof ContractResponseSchema>;
export type EndorsementResponseWithPartialSummary = z.infer<typeof EndorsementResponseWithPartialSummarySchema>;
export type EndorsementResponse = z.infer<typeof EndorsementResponseSchema>;
